import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { MAX_REGISTRATION_GUESTS } from "../../../../interfaces/models/IRegistration";
import { buildSpecialEventProgramsDisplay } from "../specialEventProgramDisplay";
import SpecialEventProgramCard from "./SpecialEventProgramCard";
import { useEditParticipantForm } from "../hooks/useEditParticipantForm";
import {
  selectEventData,
  selectRegistrationData,
} from "../editParticipant.selectors";
import { normalizeSpecialEventGuestsMap } from "../../../../utils/specialEventGuests";

export default function SpecialEventsSection (): React.ReactElement | null {
  const eventData = useSelector(selectEventData);
  const registrationData = useSelector(selectRegistrationData);
  const persistedSpecialEventGuests =
    normalizeSpecialEventGuestsMap(registrationData);
  const {
    specialEventPrograms,
    selectedSpecialEventIdSet,
    specialEventGuestsMap,
    guests,
    showGuestValidationErrors,
    registrantFieldsDisabled,
    toggleSpecialEvent,
    updateSpecialEventGuest,
    removeSpecialEventGuest,
    copyMainGuestsToSpecialEvent,
    addSpecialEventGuest,
  } = useEditParticipantForm();

  const programDisplays = useMemo(
    () => buildSpecialEventProgramsDisplay(specialEventPrograms, eventData),
    [eventData, specialEventPrograms],
  );

  if (programDisplays.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="w-full text-secondary">Special Events</h3>
      <p className="text-sm text-gray-font">
        Optional. Assign this registrant to any special events below.
        Each special event can include up to {MAX_REGISTRATION_GUESTS}{" "}
        guests (same as the main event or different).
      </p>
      <div className="flex flex-col gap-6">
        {programDisplays.map((display) => (
          <SpecialEventProgramCard
            key={display.programId}
            display={display}
            isSelected={selectedSpecialEventIdSet.has(display.programId)}
            programGuests={specialEventGuestsMap[display.programId] ?? []}
            persistedProgramGuests={
              persistedSpecialEventGuests[display.programId] ?? []
            }
            guests={guests}
            showGuestValidationErrors={showGuestValidationErrors}
            registrantFieldsDisabled={registrantFieldsDisabled}
            onToggle={toggleSpecialEvent}
            onUpdateGuest={updateSpecialEventGuest}
            onRemoveGuest={removeSpecialEventGuest}
            onCopyMainGuests={copyMainGuestsToSpecialEvent}
            onAddGuest={addSpecialEventGuest}
          />
        ))}
      </div>
    </div>
  );
}
